import { useState } from "react";
import CrudTable from "../components/CrudTable";
import { FileCCE } from "../components/ui";
import PDFButton from "../components/PDFButton";

const TABS = [
  { key: "federal", label: "Federal" },
  { key: "estatal", label: "Estatal" },
  { key: "municipal", label: "Municipal" },
  { key: "especiales", label: "Especiales" },
  { key: "eval", label: "Evaluación de Requisitos" },
  { key: "monitor", label: "Monitor Normativo" },
  { key: "sanctions", label: "Multas y Sanciones" },
  { key: "reports", label: "Informes Periódicos" },
];

export default function LegalRequirements() {
  const [tab, setTab] = useState("federal");

  return (
    <div data-testid="legal-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Requisitos Legales</h1>
        <PDFButton endpoint="/pdf/module/legal_permits" filename="reporte_legal.pdf" testId="legal-pdf" />
      </div>

      <div className="tabs-bar">
        {TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`legal-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "federal" && <PermitsTab scope="Federal" />}
      {tab === "estatal" && <PermitsTab scope="Estatal" />}
      {tab === "municipal" && <PermitsTab scope="Municipal" />}
      {tab === "especiales" && <PermitsTab scope="Especiales" />}
      {tab === "eval" && <EvalTab />}
      {tab === "monitor" && <MonitorTab />}
      {tab === "sanctions" && <SanctionsTab />}
      {tab === "reports" && <ReportsTab />}
    </div>
  );
}

function PermitsTab({ scope }) {
  const [selected, setSelected] = useState(null);
  return (
    <div>
      <CrudTable
        collection="legal_permits"
        filters={{ scope }}
        testIdPrefix={`permits-${scope}`}
        fields={[
          { key: "name", label: "Nombre del permiso", required: true, full: true },
          { key: "organism", label: "Organismo", required: true },
          { key: "permit_number", label: "# Oficio" },
          { key: "issued_at", label: "Emisión", type: "date" },
          { key: "expires_at", label: "Vencimiento", type: "date" },
          { key: "responsible_tech", label: "Responsable técnico" },
          { key: "responsible_admin", label: "Responsable administrativo" },
          { key: "compliance_pct", label: "Cumplimiento %", type: "number" },
          { key: "status", label: "Status", type: "select", options: ["Vigente", "Por vencer", "Vencido"] },
          { key: "scope", label: "Ámbito", default: scope, hidden: true, hiddenInForm: true },
        ]}
        onRowClick={(row) => setSelected(row)}
      />
      {selected && (
        <div className="card mt-4" data-testid="permit-fvi">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="card-h1" style={{ margin: 0, fontSize: 18 }}>{selected.name}</h3>
              <div className="text-xs text-dim mt-2">{selected.organism} · {selected.permit_number}</div>
            </div>
            <button className="btn btn-sm" onClick={() => setSelected(null)}>Cerrar</button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="kpi-card"><div className="label">Vencimiento</div><div className="value text-lg">{selected.expires_at || "-"}</div></div>
            <div className="kpi-card"><div className="label">Resp. técnico</div><div className="value text-lg">{selected.responsible_tech || "-"}</div></div>
            <div className="kpi-card"><div className="label">Cumplimiento</div><div className="value text-lg">{selected.compliance_pct || 0}%</div></div>
          </div>
          <h4 className="text-sm font-semibold mb-2">Condicionantes / Requisitos</h4>
          <CrudTable
            collection="legal_requirements"
            filters={{ permit_id: selected.id }}
            parent={{ id: selected.id, field: "permit_id" }}
            testIdPrefix={`req-${selected.id}`}
            fields={[
              { key: "number", label: "#", type: "number" },
              { key: "name", label: "Requisito", required: true, full: true },
              { key: "scope", label: "Ámbito" },
              { key: "responsible", label: "Responsable" },
              { key: "compliance_status", label: "Status", type: "select", options: ["Cumple", "No cumple", "Parcial", "No evaluado"] },
              { key: "next_action", label: "Próxima acción" },
              { key: "last_eval", label: "Última evaluación", type: "date" },
            ]}
          />
          <h4 className="text-sm font-semibold mb-2 mt-4">Documentos y evidencias</h4>
          <FileCCE module="legal_permits" parentId={selected.id} />
        </div>
      )}
    </div>
  );
}

function EvalTab() {
  return (
    <CrudTable
      collection="legal_requirements"
      testIdPrefix="eval-req"
      fields={[
        { key: "code", label: "Código" },
        { key: "scope", label: "Ámbito", type: "select", options: ["Federal", "Estatal", "Municipal", "Otro"], required: true },
        { key: "authority", label: "Autoridad" },
        { key: "name", label: "Nombre del permiso", required: true },
        { key: "law", label: "Ley o reglamento" },
        { key: "description", label: "Descripción", type: "textarea", full: true },
        { key: "compliance_status", label: "Estado", type: "select", options: ["Cumple", "No cumple", "Parcial", "No evaluado"], default: "No evaluado" },
        { key: "last_eval", label: "Última evaluación", type: "date" },
        { key: "next_eval", label: "Próxima evaluación", type: "date" },
        { key: "comments", label: "Comentarios", type: "textarea", full: true },
      ]}
    />
  );
}

function MonitorTab() {
  return (
    <CrudTable
      collection="norm_monitor"
      testIdPrefix="norm-monitor"
      fields={[
        { key: "name", label: "Norma", required: true },
        { key: "version", label: "Versión vigente" },
        { key: "last_review", label: "Última revisión", type: "date" },
        { key: "next_review", label: "Próxima revisión", type: "date" },
        { key: "responsible", label: "Responsable" },
        { key: "status", label: "Status", type: "select", options: ["Sin cambios", "Cambio detectado"], default: "Sin cambios" },
      ]}
    />
  );
}

function SanctionsTab() {
  return (
    <div>
      <CrudTable
        collection="sanctions"
        title="Multas y Sanciones"
        testIdPrefix="sanctions"
        fields={[
          { key: "folio", label: "Folio oficio", required: true },
          { key: "date", label: "Fecha", type: "date" },
          { key: "authority", label: "Autoridad" },
          { key: "type", label: "Tipo" },
          { key: "description", label: "Descripción", type: "textarea", full: true },
          { key: "amount", label: "Monto $", type: "number" },
          { key: "deadline", label: "Plazo atención", type: "date" },
          { key: "responsible", label: "Responsable" },
          { key: "status", label: "Status", type: "select", options: ["Notificada", "En atención", "Pagada", "Recurrida", "Cerrada"] },
        ]}
      />
      <h3 className="card-h1 mt-6" style={{ fontSize: 16 }}>Lecciones Aprendidas</h3>
      <CrudTable
        collection="lessons_learned"
        testIdPrefix="lessons"
        fields={[
          { key: "code", label: "Código (LA-####)" },
          { key: "description", label: "Descripción", type: "textarea", full: true, required: true },
          { key: "year", label: "Año", type: "number" },
          { key: "authority", label: "Autoridad sancionadora" },
          { key: "root_cause", label: "Causa raíz", type: "textarea", full: true },
          { key: "corrective_measure", label: "Medida correctiva", type: "textarea", full: true },
          { key: "affected_module", label: "Módulo afectado" },
          { key: "communicated", label: "Comunicado al equipo", type: "checkbox" },
          { key: "comments", label: "Comentarios", type: "textarea", full: true },
        ]}
      />
    </div>
  );
}

function ReportsTab() {
  return (
    <CrudTable
      collection="periodic_reports"
      testIdPrefix="periodic-reports"
      fields={[
        { key: "name", label: "Nombre del informe", required: true, full: true },
        { key: "authority", label: "Autoridad" },
        { key: "permit_relation", label: "Permiso relacionado" },
        { key: "frequency", label: "Frecuencia", type: "select", options: ["Mensual", "Trimestral", "Semestral", "Anual", "Bianual"] },
        { key: "scheduled_date", label: "Fecha programada", type: "date" },
        { key: "next_date", label: "Próxima fecha", type: "date" },
        { key: "delivered_by", label: "Entregado por" },
        { key: "status", label: "Status", type: "select", options: ["Pendiente", "En proceso", "Entregado", "Vencido"] },
      ]}
    />
  );
}
